import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, AppDispatch } from '../store'
import { fetchInvitations, markInvitationsAsRead } from '../store/invitationsSlice'
import InvitationCard from '../components/InvitationCard'
import { useTranslation } from '../hooks/use-i18n'
import { isInvitationRecipient } from '../types/invitation'

const Invitations: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { t } = useTranslation()
  const { invitations, loading, unreadCount } = useSelector((state: RootState) => state.invitations)
  const { user } = useSelector((state: RootState) => state.auth)
  const [viewType, setViewType] = useState<'received' | 'sent'>('received')

  useEffect(() => {
    dispatch(fetchInvitations())
  }, [dispatch])

  useEffect(() => {
    // Mark invitations as read when user visits the page
    if (unreadCount > 0) {
      dispatch(markInvitationsAsRead())
    }
  }, [dispatch, unreadCount])

  const filteredInvitations = invitations.filter(invitation => {
    if (viewType === 'received') {
      return isInvitationRecipient(invitation, user)
    }
    return invitation.sender._id === user?._id
  })

  const receivedInvitations = invitations.filter(inv => isInvitationRecipient(inv, user))
  const sentInvitations = invitations.filter(inv => inv.sender._id === user?._id)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-400 mx-auto mb-4"></div>
          <p className="text-white/80">Loading invitations...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 bg-gradient-to-r from-sky-400 to-blue-600 bg-clip-text text-transparent">
            {t('invitations.title')}
          </h1>
          <p className="text-white/80 text-sm sm:text-base lg:text-lg">
            {t('invitations.subtitle')}
          </p>
        </div>

        {/* Toggle Buttons */}
        <div className="flex justify-center mb-8">
          <div className="bg-white/10 rounded-xl p-1 flex">
            <button
              onClick={() => setViewType('received')}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 ${
                viewType === 'received'
                  ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              {t('invitations.received')} ({receivedInvitations.length})
            </button>
            <button
              onClick={() => setViewType('sent')}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 ${
                viewType === 'sent'
                  ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              {t('invitations.sent')} ({sentInvitations.length})
            </button>
          </div>
        </div>

        {/* Invitations List */}
        <div className="space-y-4">
          {filteredInvitations.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4 opacity-50">📧</div>
              <h3 className="text-xl font-semibold text-white mb-2">
                No {viewType} invitations
              </h3>
              <p className="text-white/60">
                {viewType === 'received'
                  ? t('invitations.noPending')
                  : t('invitations.noneSent')
                }
              </p>
            </div>
          ) : (
            filteredInvitations.map(invitation => (
              <InvitationCard key={invitation._id} invitation={invitation} />
            ))
          )}
        </div>

        {/* Stats */}
        {invitations.length > 0 && (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="blue-sky-card rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-sky-400 mb-1">
                {receivedInvitations.filter(inv => inv.status === 'pending').length}
              </div>
              <div className="text-white/80 text-sm">Pending</div>
            </div>
            <div className="blue-sky-card rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-green-400 mb-1">
                {receivedInvitations.filter(inv => inv.status === 'accepted').length}
              </div>
              <div className="text-white/80 text-sm">Accepted</div>
            </div>
            <div className="blue-sky-card rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-red-400 mb-1">
                {receivedInvitations.filter(inv => inv.status === 'rejected').length}
              </div>
              <div className="text-white/80 text-sm">Rejected</div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Invitations
